import type { CatalogueEntry, CatalogueStyle, LegendEntry } from "../../catalogue/types";
import type { LayerSnapshot } from "../../core/store";

export type SwatchShape = "point" | "line" | "polygon" | "icon" | "tile";

export function shapeOf(entry: CatalogueEntry | null, snapshot: LayerSnapshot | null): SwatchShape {
  if (entry === null) return snapshot?.kind === "track" ? "line" : "polygon";
  if (entry.kind === "tile" || entry.kind === "special") return "tile";
  if (entry.style?.icon) return "icon";

  const counts = entry.source?.counts ?? null;

  if (counts !== null && counts.total > 0) {
    if (counts.point >= counts.line && counts.point >= counts.polygon) return "point";
    if (counts.line >= counts.polygon) return "line";

    return "polygon";
  }

  if (entry.style?.brush) return "polygon";
  if (entry.style?.pen) return "line";

  return "point";
}

export function colourOf(style: CatalogueStyle | null, fallback = "var(--fxmap-muted)"): string {
  return style?.brush?.color ?? style?.pen?.color ?? fallback;
}

export type SwatchProps = {
  entry?: CatalogueEntry | null;
  snapshot?: LayerSnapshot | null;
  legend?: LegendEntry | null;
  color?: string | null;
  className?: string;
};

export function Swatch({ entry = null, snapshot = null, legend = null, color = null, className }: SwatchProps) {
  const iconUrl = legend?.iconUrl ?? entry?.style?.icon?.url ?? null;

  if (iconUrl !== null) return <img className={["fxmap-swatch", "fxmap-swatch-icon", className ?? ""].filter(Boolean).join(" ")} src={iconUrl} alt="" draggable={false} />;

  const shape: SwatchShape = legend?.shape ?? shapeOf(entry, snapshot);
  const fill = color ?? legend?.fillColor ?? legend?.color ?? colourOf(entry?.style ?? null);
  const stroke = legend?.color ?? entry?.style?.pen?.color ?? fill;

  if (shape === "tile") return <span className={["fxmap-swatch", "fxmap-swatch-tile", className ?? ""].filter(Boolean).join(" ")} aria-hidden="true" />;

  return <span className={["fxmap-swatch", `fxmap-swatch-${shape}`, className ?? ""].filter(Boolean).join(" ")} style={{ background: shape === "line" ? stroke : fill, borderColor: stroke }} aria-hidden="true" />;
}
